import { useContact } from '../../api/contacts.js';
import { RechercheContact } from '../ventes/RechercheContact.js';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Select, Textarea } from '@artosera/ui';
import {
  useCreerConditionsArtiste,
  useModifierConditionsArtiste,
  type ConditionsArtiste,
  type CorpsConditions,
} from '../../api/comptesArtistes.js';
import { Field } from '../Field.js';
import { messageCompte } from './compteUi.js';
import styles from './ComptesArtistes.module.css';

export function ConditionsFormulaire({
  galerieSlug,
  artisteId,
  version,
  conditions,
  onEnregistre,
}: {
  galerieSlug: string;
  artisteId: string;
  version?: ConditionsArtiste;
  conditions: ConditionsArtiste | null;
  onEnregistre: () => void;
}) {
  const { t } = useTranslation(['regie', 'commun']);
  const label = (cle: string) => t(`regie:comptesArtistes.${cle}`);
  const initiale = version ?? conditions;
  const [effetLe, setEffetLe] = useState(
    version?.effetLe.slice(0, 10) ??
      new Intl.DateTimeFormat('fr-CA', { timeZone: 'Europe/Paris' }).format(new Date()),
  );
  const [mode, setMode] = useState<CorpsConditions['mode']>(initiale?.mode ?? 'commission');
  const [taux, setTaux] = useState(String((initiale?.tauxCommissionPdb ?? 0) / 100));
  const [fait, setFait] = useState<CorpsConditions['faitGenerateur']>(
    initiale?.faitGenerateur ?? 'encaissement',
  );
  const [periodicite, setPeriodicite] = useState<CorpsConditions['periodicite']>(
    initiale?.periodicite ?? 'mensuelle',
  );
  const [frais, setFrais] = useState(initiale?.fraisParDefaut ?? '');
  const [contact, setContact] = useState(initiale?.contactId ?? '');
  const destinataire = useContact(galerieSlug, contact || null);
  const [erreur, setErreur] = useState('');
  const creer = useCreerConditionsArtiste(galerieSlug);
  const modifier = useModifierConditionsArtiste(galerieSlug);
  return (
    <form
      className={styles.formulaire}
      onSubmit={(e) => {
        e.preventDefault();
        setErreur('');
        const corps: Omit<CorpsConditions, 'artisteId'> = {
          effetLe,
          mode,
          ...(mode === 'commission' ? { tauxCommissionPdb: Math.round(Number(taux) * 100) } : {}),
          faitGenerateur: fait,
          periodicite,
          fraisParDefaut: frais,
          contactId: contact || null,
        };
        const action = version
          ? modifier.mutateAsync({ id: version.id, corps })
          : creer.mutateAsync({ ...corps, artisteId });
        void action
          .then(onEnregistre)
          .catch((e: unknown) => setErreur(messageCompte(e, t('commun:erreurReseau'))));
      }}
    >
      <Field htmlFor="conditions-effet" label={label('effetLe')}>
        <Input
          id="conditions-effet"
          type="date"
          required
          value={effetLe}
          onChange={(e) => setEffetLe(e.target.value)}
        />
      </Field>
      <Field htmlFor="conditions-mode" label={label('mode')}>
        <Select
          id="conditions-mode"
          value={mode}
          onValueChange={(v) => setMode(v as typeof mode)}
          options={['commission', 'prix_artiste'].map((v) => ({
            value: v,
            label: label(`modes.${v}`),
          }))}
        />
      </Field>
      {mode === 'commission' && (
        <Field htmlFor="conditions-taux" label={label('taux')}>
          <Input
            id="conditions-taux"
            type="number"
            min="0"
            max="100"
            step="0.01"
            required
            value={taux}
            onChange={(e) => setTaux(e.target.value)}
          />
        </Field>
      )}
      <Field htmlFor="conditions-fait" label={label('faitGenerateur')}>
        <Select
          id="conditions-fait"
          value={fait}
          onValueChange={(v) => setFait(v as typeof fait)}
          options={['encaissement', 'emission'].map((v) => ({
            value: v,
            label: label(`faits.${v}`),
          }))}
        />
      </Field>
      <Field htmlFor="conditions-periodicite" label={label('periodicite')}>
        <Select
          id="conditions-periodicite"
          value={periodicite}
          onValueChange={(v) => setPeriodicite(v as typeof periodicite)}
          options={['mensuelle', 'trimestrielle', 'a_la_demande'].map((v) => ({
            value: v,
            label: label(`periodicites.${v}`),
          }))}
        />
      </Field>
      <Field htmlFor="conditions-frais" label={label('fraisParDefaut')}>
        <Textarea
          id="conditions-frais"
          maxLength={500}
          value={frais}
          onChange={(e) => setFrais(e.target.value)}
        />
      </Field>
      <div>
        <p>{label('contact')}</p>
        <RechercheContact
          galerieSlug={galerieSlug}
          contact={destinataire.data?.contact ?? null}
          onChoisir={(c) => setContact(c.id)}
          onEffacer={() => setContact('')}
        />
      </div>
      {destinataire.isError && (
        <p role="alert">{messageCompte(destinataire.error, t('commun:erreurReseau'))}</p>
      )}
      {erreur && <p role="alert">{erreur}</p>}
      <Button type="submit" loading={creer.isPending || modifier.isPending}>
        {label('enregistrerConditions')}
      </Button>
    </form>
  );
}
